import { createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import difference from 'lodash/difference';
import shuffle from 'lodash/shuffle';
import {
  Donation,
  DonationTarget,
  PreviousSubscription,
  Provider,
  Subscription,
} from '../../../types/PaymentTypes';
import {
  createSubscription,
  Currency,
  deleteStripePaymentMethod,
  getAmountSuggestions,
  getCurrencies,
  getImpactersWithStatus,
  getLocale,
  getStripePaymentMethods,
  getUserPortfolio,
  getUserSubscriptions,
  previewSubscription,
  PreviewSubscriptionResponse,
  UserSubscription,
} from '../../api';
import { isApiError } from '../../error';
import { handlePaymentSetupIntent } from '../../stripeHelpers';
import { captureException, Severity } from '../../tracking/sentry/sentry';
import { setUnverifiedUserId } from '../authentication';
import { AuthModuleState } from '../authentication/createModule';
import { getUnverifiedUser } from '../authentication/selectors';
import { RootState } from '../store';
import { completePaymentFlow } from './commonPaymentActions';
import {
  getActiveDonation,
  getActiveProviderData,
  getAffiliate,
  getPreviousSubscription,
} from './paymentSelectors';

export const getKlarnaCurrencies = (country?: string) => {
  if (country) {
    const upperCaseCountry = country.toUpperCase();
    if (upperCaseCountry === 'NO') {
      return [{ code: 'NOK', name: 'Krone', cents: 100 }];
    } else if (upperCaseCountry === 'DK') {
      return [{ code: 'DKK', name: 'Kroner', cents: 100 }];
    } else if (upperCaseCountry === 'SE') {
      return [{ code: 'SEK', name: 'Kronor', cents: 100 }];
    } else if (upperCaseCountry === 'DE') {
      return [{ code: 'EUR', name: 'Euro', cents: 100 }];
    } else if (upperCaseCountry === 'NL') {
      return [{ code: 'EUR', name: 'Euro', cents: 100 }];
    } else if (upperCaseCountry === 'GB') {
      return [{ code: 'GBP', name: 'Pounds', cents: 100 }];
    } else if (upperCaseCountry === 'FI') {
      return [{ code: 'EUR', name: 'Euro', cents: 100 }];
    } else if (upperCaseCountry === 'AT') {
      return [{ code: 'EUR', name: 'Euro', cents: 100 }];
    }
  }
  return [];
};

type FetchPaymentData = {
  country?: string;
  provider: Provider;
};

export const fetchPaymentData = createAsyncThunk<{ defaultCurrencyCode: string }, FetchPaymentData>(
  'payment/fetchAmountData',
  async ({ provider, country }, { dispatch }) => {
    let defaultCurrencyCode;

    if (provider === 'klarna') {
      defaultCurrencyCode = getKlarnaCurrencies(country)[0].code;
    } else {
      const { result: currencies } = await getCurrencies();
      if (currencies.length === 1) {
        defaultCurrencyCode = currencies[0].code;
      } else {
        const localeCurrencyCode = (await getLocale()).suggestion.defaultCurrencyCode;
        defaultCurrencyCode = currencies.some(({ code }) => code == localeCurrencyCode)
          ? localeCurrencyCode
          : 'USD';
      }
    }

    dispatch(fetchPaymentOptions({ currencyCode: defaultCurrencyCode }));
    dispatch(fetchCurrencies({ provider, country }));

    return { defaultCurrencyCode };
  },
);

type FetchPaymentOptions = {
  currencyCode: string;
};

export const fetchPaymentOptions = createAsyncThunk(
  'payment/fetchPaymentOptions',
  async ({ currencyCode }: FetchPaymentOptions) => {
    const amountOptionsResponse = await getAmountSuggestions({ currencyCode });
    return { currencyCode, options: amountOptionsResponse.result };
  },
);

type FetchCurrencies = {
  provider: Provider;
  country?: string;
};

export const fetchCurrencies = createAsyncThunk(
  'payment/fetchCurrencies',
  async ({ provider, country }: FetchCurrencies) => {
    let currencies: Omit<Currency, 'providers'>[] = [];
    //TODO: server side filtering and fetch Klarna from server side as well (with country flag that doesn't exist today)
    if (provider === 'stripe') {
      const currenciesResponse = await getCurrencies();
      currencies = currenciesResponse.result.filter(({ providers }) =>
        providers.includes(provider),
      );
    } else if (provider === 'klarna') {
      currencies = getKlarnaCurrencies(country);
    }
    return { currencies, provider, country };
  },
);

type FetchPaymentMethods = {
  provider: Provider;
};

export const fetchPaymentMethods = createAsyncThunk(
  'payment/fetchPaymentMethods',
  async ({ provider }: FetchPaymentMethods) => {
    switch (provider) {
      case 'stripe':
        return await fetchStripePaymentMethods();
      default:
        captureException(`fetchPaymentMethods not supported for provider ${provider}`);
        return;
    }
  },
);

const fetchStripePaymentMethods = async () => {
  const { result, publicKey } = await getStripePaymentMethods();
  return {
    paymentMethods: result,
    publicKey,
  };
};

export const fetchUserSubscription = createAsyncThunk<
  { subscription: PreviousSubscription | undefined },
  void,
  {
    state: RootState & AuthModuleState;
    rejectValue: { alreadySet?: boolean };
  }
>('payment/fetchUserSubscription', async (_, { getState, rejectWithValue }) => {
  const state = await getState();

  const preSubscription = getPreviousSubscription(state);
  if (preSubscription) {
    return rejectWithValue({ alreadySet: true });
  }

  const unverifiedUser = getUnverifiedUser(state);
  const { result } = (unverifiedUser && { result: [] }) || (await getUserSubscriptions());
  const subscription = findValidSubscription({ subscriptions: result });
  if (!subscription || !subscription.currencyCode) {
    return {
      subscription: undefined,
    };
  }

  return {
    subscription: {
      currency: subscription?.currency,
      currencyCode: subscription?.currencyCode,
      amount: getExistingAmount({ subscription }),
      impacterIds: getExistingImpacters({ subscription }),
    },
  };
});

export const prepareSubscriptionForVerifiedUser = createAsyncThunk<
  void,
  void,
  {
    state: RootState & AuthModuleState;
  }
>('payment/prepareSubscriptionForVerifiedUser', async (_, { dispatch }) => {
  const update = await Promise.all([
    dispatch(updateDonationImpacters()),
    dispatch(fetchUserSubscription()),
  ]);

  update.forEach((task) => {
    try {
      unwrapResult(task);
    } catch (error) {
      // @ts-expect-error FIXME: handle error types explicitly for improved error handling
      if (!('alreadySet' in error)) {
        throw error;
      }
    }
  });
});

export const prepareSubscription = createAsyncThunk<
  void,
  void,
  {
    state: RootState & AuthModuleState;
    rejectValue: { currencyMismatched?: UserSubscription['currency'] };
  }
>('payment/prepareSubscription', async (_, { getState, rejectWithValue, dispatch }) => {
  const result = await dispatch(prepareSubscriptionForVerifiedUser());
  unwrapResult(result);

  const state = await getState();
  const donation = getActiveDonation(state);

  if (!donation) {
    captureException('Trying to prepare a subscription without a donation');
    return rejectWithValue({});
  }

  const previousSubscription = getPreviousSubscription(state);
  if (previousSubscription && previousSubscription.currencyCode !== donation.currencyCode) {
    captureException(`Trying to update a subscription with different currencies`, {
      extra: { donation, previousSubscription },
      level: Severity.Warning,
    });
    return rejectWithValue({
      currencyMismatched: previousSubscription.currency as UserSubscription['currency'],
    });
  }

  dispatch(updateSubscriptionPreview());
});

export const updateDonationImpacters = createAsyncThunk<
  { impacterIds: number[] },
  void,
  {
    state: RootState & AuthModuleState;
    rejectValue: { noImpacter?: boolean; alreadySet?: boolean };
  }
>('payment/updateDonationImpacters', async (_, { getState, rejectWithValue }) => {
  const state = await getState();
  const donation = getActiveDonation(state);
  if (!donation) {
    captureException('Trying to update donation impacters without a donation');
    return rejectWithValue({});
  }
  if (donation.impacterIds) {
    return rejectWithValue({ alreadySet: true });
  }
  const unverifiedUser = getUnverifiedUser(state);
  const impacterIds = await getImpacterIds({
    target: donation.target,
    unverifiedUser: !!unverifiedUser,
    tagNames: donation.tagNames,
  });
  if (impacterIds.length) {
    return { impacterIds };
  }
  return rejectWithValue({ noImpacter: true });
});

const getImpacterIds = async ({
  target,
  tagNames,
  unverifiedUser,
}: {
  target?: DonationTarget;
  tagNames?: string[];
  unverifiedUser?: boolean;
}) => {
  let errorMessage;
  switch (target) {
    case 'quickfix':
      return getRandomImpacterIdsToSupport({ unverifiedUser });
    case 'category':
      return getRandomImpacterIdsToSupport({ tagNames, unverifiedUser });
    default:
      errorMessage = `Target ${target} not supported`;
      captureException(errorMessage);
      throw new Error(errorMessage);
  }
};

const getRandomImpacterIdsToSupport = async ({
  tagNames,
  unverifiedUser,
}: {
  tagNames?: string[];
  unverifiedUser?: boolean;
}) => {
  const [{ result: impacters }, { result: portfolios }] = await Promise.all([
    getImpactersWithStatus({ fields: 'id', status: 'ACTIVE', anyTagNames: tagNames }),
    unverifiedUser ? { result: [] } : getUserPortfolio(),
  ]);
  const impacterIds = impacters.map(({ id }) => id);
  const diff = portfolios.length
    ? difference(
        impacterIds,
        (portfolios[0].items || []).map(({ impacterId }) => impacterId),
      )
    : impacterIds;
  return shuffle(diff).slice(0, 5);
};

export const updateSubscription = createAsyncThunk<
  { subscription: Subscription; donation: Donation },
  void,
  {
    state: RootState & AuthModuleState;
    rejectValue: { userMessage?: string };
  }
>('payment/updateSubscription', async (_, { getState, rejectWithValue, dispatch }) => {
  const state = await getState();
  const donation = getActiveDonation(state);
  if (!donation) {
    captureException('Trying to update a subscription without a donation');
    return rejectWithValue({});
  }
  const affiliate = getAffiliate(state);
  const { currencyCode, paymentMethodId } = donation;
  if (!currencyCode || !paymentMethodId) {
    captureException(
      `Trying to update a subscription without a currency code, an amount, a payment method id or impacterIds`,
      {
        extra: donation,
      },
    );
    return rejectWithValue({});
  }

  let error;
  try {
    let user;
    if (getUnverifiedUser(state)) {
      const { getFirebaseToken } = await import('../../auth');
      const idToken = await getFirebaseToken(true);
      user = {
        firebaseToken: idToken?.token,
      };
    }
    const item = unwrapResult(await dispatch(getSubscriptionItems()));
    const {
      subscription,
      providerResponse,
      user: userResponse,
    } = await createSubscription({
      currencyCode,
      items: [item],
      affiliates: affiliate
        ? [
            {
              affiliateName: affiliate.name,
              publishable: affiliate.publishable,
              message: affiliate.message,
            },
          ]
        : undefined,
      paymentMethodId,
      user,
    });

    try {
      const pendingSetupIntent = providerResponse?.pendingSetupIntent;
      if (pendingSetupIntent) {
        await handleCurrentProvider({ state, pendingSetupIntent });
      }
      if (userResponse?.id) {
        dispatch(setUnverifiedUserId(userResponse?.id));
      }

      dispatch(completePaymentFlow());
      return { subscription, donation };
    } catch (err) {
      deleteStripePaymentMethod(paymentMethodId);
      error = err;
    }
  } catch (err) {
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    if (!isApiError(err)) captureException(err.message);

    error = err;
  }
  // @ts-expect-error FIXME: handle error types explicitly for improved error handling
  if (isApiError(error)) return rejectWithValue({ userMessage: error.customerMessage });

  return rejectWithValue({});
});

const handleCurrentProvider = async ({
  state,
  pendingSetupIntent,
}: {
  state: RootState;
  pendingSetupIntent: { status: string; client_secret: string };
}) => {
  const providerData = getActiveProviderData(state);
  if (!providerData) {
    throw new Error('No Provider data');
  }
  if (providerData.id === 'stripe') {
    if (!providerData.publicKey) {
      throw new Error('No public key for stripe');
    }
    const { loadStripe } = await import('@stripe/stripe-js');
    const stripe = await loadStripe(providerData?.publicKey);
    if (!stripe) {
      throw new Error('Stripe SDK did not load properly');
    }
    await handlePaymentSetupIntent({
      stripe,
      status: pendingSetupIntent.status,
      setupIntentClientSecret: pendingSetupIntent.client_secret,
    });
  }
};

export const updateSubscriptionPreview = createAsyncThunk<
  PreviewSubscriptionResponse,
  void,
  { state: RootState; rejectValue: void }
>('payment/updateSubscriptionPreview', async (_, { getState, rejectWithValue, dispatch }) => {
  const state = await getState();
  const donation = getActiveDonation(state);
  if (!donation) {
    captureException('Trying to update a subscription without a donation');
    return rejectWithValue();
  }
  const { currencyCode, provider } = donation;
  if (!currencyCode || !provider) {
    captureException(`Trying to preview a subscription without a proper donation`, {
      extra: donation,
    });
    return rejectWithValue();
  }

  const item = unwrapResult(await dispatch(getSubscriptionItems()));
  const subscription = await previewSubscription({
    currencyCode,
    paymentProvider: provider,
    items: [item],
  });
  return subscription;
});

const findValidSubscription = ({ subscriptions }: { subscriptions: UserSubscription[] }) => {
  const validSubscriptions = subscriptions.filter((subscription) => {
    if (subscription.status !== 'ACTIVE') {
      return false;
    }
    if (subscription.providerData) {
      // ignore subscriptions that don't come from personal payment
      return false;
    }
    if (!findValidSubscriptionItem({ items: subscription.items })) {
      return false;
    }
    if (subscription.paymentProviderName !== 'stripe') {
      captureException('found a valid subscription with a paymentProvider different than stripe', {
        extra: {
          subscription,
        },
        level: Severity.Warning,
      });
    }
    return true;
  });
  if (validSubscriptions?.length > 1) {
    captureException('found multiple valid subscriptions', {
      extra: {
        validSubscriptions,
      },
      level: Severity.Error,
    });
    throw new Error();
  }
  return validSubscriptions?.[0];
};

const findValidSubscriptionItem = ({ items }: { items: UserSubscription['items'] }) =>
  items.find(({ type }) => type === 'DEFAULT');

const getExistingAmount = ({ subscription }: { subscription?: UserSubscription }): number => {
  if (!subscription) {
    return 0;
  }
  const validItem = findValidSubscriptionItem({ items: subscription.items });
  if (!validItem) {
    return 0;
  }
  return validItem.totalAmount;
};

const getExistingImpacters = ({ subscription }: { subscription?: UserSubscription }): number[] => {
  if (!subscription) {
    return [];
  }
  const validItem = findValidSubscriptionItem({ items: subscription.items });
  if (!validItem) {
    return [];
  }
  return validItem.impacterIds || [];
};

export const getSubscriptionItems = createAsyncThunk<
  { amount: number },
  void,
  { state: RootState; rejectValue: void }
>('payment/getSubscriptionItems', async (_, { getState, rejectWithValue }) => {
  const state = await getState();
  const donation = getActiveDonation(state);
  if (!donation || !donation.currencyCode || !donation.impacterIds || !donation.amount) {
    captureException(`Trying to build subscription items without a proper donation`, {
      extra: donation,
    });
    return rejectWithValue();
  }

  const subscription = getPreviousSubscription(state);
  if (subscription && donation.currencyCode !== subscription.currencyCode) {
    return rejectWithValue();
  }

  return {
    amount: donation.amount + (subscription?.amount || 0),
  };
});
